import { useState, useMemo } from 'react'
import { Card } from '@trafiflow/shared-ui'
import {
  LayoutGrid,
  Plus,
  Clock,
  Image,
  Trash2,
  AlertCircle,
  Film,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useStoryboards, useDeleteStoryboard } from './storyboard/hooks/useStoryboards'
import type { StoryboardAsset } from './storyboard/storyboards.service'

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHours < 1) return 'just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch {
    return 'Unknown'
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'ready':
      return 'Completed'
    default:
      return 'In Progress'
  }
}

function StoryboardCard({
  storyboard,
  onClick,
  onDelete,
}: {
  storyboard: StoryboardAsset
  onClick: () => void
  onDelete: () => void
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Thumbnail priority: source frames (compressed original video) → benchmark frames
  const thumbnailUrl = useMemo(() => {
    const hasUsableImage = (img?: string) =>
      !!img && img.trim() !== '' && (img.startsWith('data:') || img.startsWith('http'))
    const sourceFrame = storyboard.sourceFrames?.find(f => hasUsableImage(f.image))
    if (sourceFrame?.image) return sourceFrame.image
    const benchFrame = storyboard.frames?.find(f => hasUsableImage(f.image))
    if (benchFrame?.image) return benchFrame.image
    return null
  }, [storyboard.frames, storyboard.sourceFrames])

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
    setShowDeleteConfirm(false)
  }

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteConfirm(false)
  }

  const showPlaceholder = !thumbnailUrl || imageError

  return (
    <Card
      className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer group relative"
      onClick={onClick}
    >
      <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
        {showPlaceholder ? (
          <div className="flex flex-col items-center gap-2">
            <Film className="w-10 h-10 text-tf-text-muted" />
            <span className="text-xs text-tf-text-muted">{storyboard.frames?.length || 0} scenes</span>
          </div>
        ) : (
          <img
            src={thumbnailUrl}
            alt={storyboard.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        )}
        <div className="absolute top-2 right-2 px-2 py-1 bg-tf-bg-card/90 backdrop-blur-sm rounded text-xs font-medium">
          {getStatusLabel(storyboard.status)}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-medium text-tf-text mb-2 truncate">{storyboard.name}</h3>
        <div className="flex items-center justify-between text-sm text-tf-text-muted">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Image className="w-3.5 h-3.5" />
              {storyboard.frames?.length || 0} frames
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {formatDate(storyboard.updatedAt)}
            </span>
          </div>
          <button
            onClick={handleDeleteClick}
            className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-tf-text-muted transition-colors"
            title="Delete storyboard"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-black/60 flex items-center justify-center p-4"
          onClick={e => e.stopPropagation()}
        >
          <div className="bg-white rounded-lg p-4 max-w-[200px] text-center">
            <p className="text-sm text-tf-text mb-3">Delete this storyboard?</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleCancelDelete}
                className="px-3 py-1.5 text-xs rounded-md border border-tf-border hover:bg-tf-bg-hover"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-3 py-1.5 text-xs rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

function LoadingSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4" />
        <div className="h-4 bg-gray-100 rounded animate-pulse w-1/2" />
      </div>
    </Card>
  )
}

export function Storyboard() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useStoryboards({ limit: 50 })
  const deleteMutation = useDeleteStoryboard()

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  const storyboards = data?.storyboards || []

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Storyboard</h1>
          <p className="text-tf-text-muted mt-1">
            Create and visualize your ad concepts with AI-powered storyboarding
          </p>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          onClick={() => navigate('/marketing/playground/storyboard-generator')}
        >
          <Plus className="w-4 h-4" />
          <span>New Storyboard</span>
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">
            Failed to load storyboards. Please try again.
          </p>
        </div>
      ) : isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
      ) : storyboards.length === 0 ? (
        <div className="rounded-lg border border-tf-border bg-tf-bg-muted p-8 text-center">
          <LayoutGrid className="w-12 h-12 text-tf-text-muted mx-auto mb-3" />
          <h3 className="font-medium text-tf-text mb-1">No storyboards yet</h3>
          <p className="text-sm text-tf-text-muted mb-4">
            Create your first storyboard to get started
          </p>
          <button
            onClick={() => navigate('/storyboard-generator')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Create Storyboard
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {storyboards.map(storyboard => (
            <StoryboardCard
              key={storyboard.id}
              storyboard={storyboard}
              onClick={() => navigate(`/marketing/playground/storyboard/${storyboard.id}`)}
              onDelete={() => handleDelete(storyboard.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Storyboard
